import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { AllLabels, FacilityFormTypes, FormatterData, InstallationFlowType } from '../../../../constants/app.constants';
import { DateFormat, FormTypesData, KeyValueData } from '../../../../domains/common.domain';
import { CommonService } from '../../../../services/common.service';
import { AllDropdownKeys, ZerezIdMask } from '../../../jurisdiction.constants';
import { LookupOptionsData } from '../../../jurisdiction.domain';
import { FacilitiesFormService } from '../../facilities-form.service';
import { EnvironmentService } from '../../../../services/environment.service';
import { FacilitiesHiddenDropdownData } from '../../../../interface/config.interface';
import { ZerezService } from '../../../../services/zerez.service';
import { ErrorService } from '../../../../services/error/error.service';
import { DestroyableComponent } from '../../../../common/destroyable.component';
import { ToastNotificationService } from '../../../../+shared/services/toast-notification.service';
import { ValidationsService } from '../../../../services/validations.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { ConsumptionDeviceControl } from '../../facilities-form.domain';

@Component({
  providers: [ZerezService],
  selector: 'app-special-appliance',
  styleUrls: ['./special-appliance.component.scss'],
  templateUrl: './special-appliance.component.html',
})
export class SpecialApplianceComponent extends DestroyableComponent implements OnInit {
  @Input() isReadOnly: boolean = false;
  @Input() currentIndex: number = 0;
  @Input() parentIndex: number = 0;
  @Input() specialApplianceData: any = null;
  @Input() displayDeleteButton: boolean = false;
  @Input() currentFormType: FormTypesData;

  @Input() set hideAdditionalData(value: boolean) {
    this.showAdditionalData = !value;
    this.updateAdditionalDataControls();
  }

  @Output() deleteComponent: EventEmitter<number>;
  @Output() specialApplianceSelection: EventEmitter<string>;

  blockIntervalsKey: string;
  blockIntervals: LookupOptionsData[];
  isSpecialApplianceSectionRequired: boolean;
  specialApplianceKey: string;
  specialAppliances: LookupOptionsData[];
  consumptionDeviceControls: LookupOptionsData[];
  consumptionDeviceControlKey: string = AllDropdownKeys.consumptionDeviceControlType;
  specialApplianceType: string;
  specialApplianceFG: FormGroup;
  allUnits: KeyValueData;
  showAdditionalData: boolean = true;
  showSubjectToApprovalCheckbox: boolean;
  hiddenDropdownData: FacilitiesHiddenDropdownData;
  isLoading: boolean = false;
  energyProducerTypesAllowedForZerezApi = ['windturbine', 'other', 'pv_device_consumer', 'bhkw_kwk_device_consumer', 'storage_consumer'];
  energyProducerTypesAllowedForModule = ['heatpump', 'charger', 'wallbox', 'other', 'storage_consumer'];
  zerezIdMask = ZerezIdMask;
  dateFormat: DateFormat;
  currentInstallationFlow: string;

  get subjectToApprovalRequirementsMet(): boolean {
    const requirementsMet =
      this.specialApplianceType === 'wallbox' &&
      this.specialApplianceFG.controls['power'].value &&
      Number(this.specialApplianceFG.controls['power'].value) >= 11;

    if (requirementsMet) {
      this.specialApplianceFG.addControl('deviceRequiresApproval', new FormControl(this.specialApplianceData?.manufacturer || ''));
    } else {
      this.specialApplianceFG.removeControl('deviceRequiresApproval');
    }

    return requirementsMet;
  }

  get specialApplianceTypeNotOther(): boolean {
    return this.specialApplianceType != 'other';
  }

  get isPowerForm(): boolean {
    return this.currentFormType.key === FacilityFormTypes.Power.key;
  }

  get showAdditionalSkiKeyAndDateOfCommissioning(): boolean {
    return (
      this.environmentService.showMeterMountingAndChangeInstallationAdditionalData &&
      this.currentInstallationFlow === InstallationFlowType.NewInstallation &&
      this.isPowerForm
    );
  }

  get showConsumptionDeviceControl(): boolean {
    return (
      this.environmentService.showConsumptionDeviceControl &&
      this.showAdditionalSkiKeyAndDateOfCommissioning &&
      !(this.isReadOnly && this.specialApplianceData.consumption_device_control == null)
    );
  }

  constructor(
    private commonService: CommonService,
    public environmentService: EnvironmentService,
    private facilitiesFormService: FacilitiesFormService,
    private zerezService: ZerezService,
    private errorService: ErrorService,
    private toaster: ToastNotificationService,
    private localStorageService: LocalStorageService
  ) {
    super();
    this.deleteComponent = new EventEmitter<number>();
    this.specialApplianceSelection = new EventEmitter<string>();
    this.allUnits = AllLabels.units;
    this.dateFormat = FormatterData.date;
  }

  ngOnInit(): void {
    this.currentInstallationFlow = this.localStorageService.getItem('installation_flow');
    this.hiddenDropdownData = this.environmentService.facilityHiddenDropdownData;
    this.specialApplianceKey = AllDropdownKeys.SpecialConsumerType;
    this.specialAppliances = this.commonService.getDropdownData(this.specialApplianceKey);
    this.modifyDropdownCollections();
    this.blockIntervalsKey = AllDropdownKeys.SWKNBlockIntervalsType;
    this.blockIntervals = this.commonService.getDropdownData(this.blockIntervalsKey);
    this.isSpecialApplianceSectionRequired = this.environmentService.isSpecialApplianceSectionRequired;
    this.showSubjectToApprovalCheckbox = this.environmentService.showSubjectToApprovalCheckbox;
    if (this.environmentService.showConsumptionDeviceControl) {
      this.consumptionDeviceControls = this.commonService.getDropdownData(this.consumptionDeviceControlKey);
    }
    this.prepareForm();
    this.facilitiesFormService.manageSpecialApplianceFormData({
      component: 'specialAppliance',
      formData: this.specialApplianceFG,
      index: this.currentIndex,
      parentIndex: this.parentIndex,
    });

    this.facilitiesFormService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.resetData();
      }
    });
  }

  prepareForm(): void {
    const selectedAppliance =
      (this.specialApplianceData?.specialAppliance &&
        this.specialAppliances.filter(item => item.value === this.specialApplianceData.specialAppliance)[0]) ||
      null;
    this.specialApplianceType = selectedAppliance?.value || '';

    const validatorsList = this.isSpecialApplianceSectionRequired ? [Validators.required] : [];
    this.specialApplianceFG = new FormGroup({
      deviceId: new FormControl(this.specialApplianceData?.deviceId || ''),
      facilityId: new FormControl(this.specialApplianceData?.facilityId || ''),
      specialAppliance: new FormControl(this.specialApplianceType, validatorsList),
      power: new FormControl(this.specialApplianceData?.power || '', validatorsList),
      is_public_facility: new FormControl(
        { value: this.specialApplianceData?.is_public_facility, disabled: this.isReadOnly },
        validatorsList
      ),
      is_controllable_facility: new FormControl(
        { value: this.specialApplianceData?.is_controllable_facility, disabled: this.isReadOnly },
        validatorsList
      ),
    });

    if (this.specialApplianceData?.is_controllable_facility) {
      this.showOrHideAdditionalSkiKeyAndDateOfCommissioningFields(true);
    }

    this.specialApplianceFG.controls.power.valueChanges.subscribe(() => {
      this.showModuleTypeAndZerezId();
    });
    this.specialApplianceFG.controls.is_controllable_facility.valueChanges.subscribe(value => {
      this.showOrHideAdditionalSkiKeyAndDateOfCommissioningFields(value);
    });
    if (this.environmentService.showBlockIntervalsField) {
      this.specialApplianceFG.addControl('blockIntervals', new FormControl(''));
      if (this.specialApplianceType === 'heatpump') {
        this.specialApplianceFG.controls.blockIntervals.setValue(this.specialApplianceData?.blockIntervals || '');
        this.specialApplianceFG.controls.blockIntervals.updateValueAndValidity();
      }
    }

    if (this.showAdditionalData) {
      this.updateAdditionalDataControls();
    }

    if (this.specialApplianceType) {
      this.selectSpecialAppliance();
    }
  }

  selectSpecialAppliance(event: any = null): void {
    if (event) {
      this.specialApplianceType = event.value;
    }
    if (this.environmentService.showBlockIntervalsField && this.specialApplianceType !== 'heatpump') {
      this.specialApplianceFG.controls.blockIntervals.reset();
    }

    if (this.specialApplianceType === 'other') {
      this.specialApplianceFG.addControl(
        'other_appliance',
        new FormControl(this.specialApplianceData?.other_appliance || '', [Validators.required])
      );
      this.specialApplianceFG.controls.other_appliance.markAsTouched();
      this.specialApplianceFG.controls.other_appliance.updateValueAndValidity();
    } else {
      this.specialApplianceFG.removeControl('other_appliance');
    }
    this.showModuleTypeAndZerezId();
    this.specialApplianceSelection.emit(this.specialApplianceType);
  }

  removeElement(): void {
    this.deleteComponent.emit(this.currentIndex);
  }

  validateZerezId() {
    this.isLoading = true;
    const zerezId = this.specialApplianceFG.get('zerez_id').value;

    this.preventLeak(this.zerezService.getUnitByZerezId(zerezId)).subscribe({
      next: res => this.validateZerezResponse(res),
      error: err => this.validateZerezResponse(err),
    });
  }

  private showModuleTypeAndZerezId(): void {
    const isEligibleForZerezApi = this.energyProducerTypesAllowedForZerezApi.includes(this.specialApplianceType);
    const isEligibleForModule = this.energyProducerTypesAllowedForModule.includes(this.specialApplianceType);
    const validatorsList = this.isSpecialApplianceSectionRequired && this.specialApplianceTypeNotOther ? [Validators.required] : [];

    // the standard for decimal separators in Germany is comma. https://github.com/bittner-krull/gop-web/issues/2248
    const powerValue = parseFloat(this.specialApplianceFG?.controls?.power?.value.replace(',', '.'));
    const isPowerSufficient = powerValue > 4.2;

    ['zerez_id', 'zerez_id_validated', 'module_type'].forEach(control => this.specialApplianceFG.removeControl(control));

    // Handle module_type control
    if (isPowerSufficient && isEligibleForModule) {
      this.addSpecialApplianceFGControl('module_type', this.specialApplianceData?.module_type, validatorsList);
    }

    if (!this.environmentService.hideZerezId && isEligibleForZerezApi) {
      // Handle zerez_id controls
      this.addSpecialApplianceFGControl('zerez_id', this.specialApplianceData?.zerez_id || '', [
        ValidationsService.zerezIdValidator(),
        ...validatorsList,
      ]);

      this.addSpecialApplianceFGControl('zerez_id_validated', this.specialApplianceData?.zerez_id ? true : null, validatorsList);

      // Setup zerez_id validation subscriptions
      this.specialApplianceFG.controls.zerez_id.valueChanges.subscribe(() => {
        this.specialApplianceFG.get('zerez_id_validated').setValue(null);
      });
    }
  }

  // Helper method to reduce repetition
  private addSpecialApplianceFGControl(controlName: string, value: any, validators: any[]): void {
    this.specialApplianceFG.addControl(controlName, new FormControl({ value, disabled: this.isReadOnly }, validators));
  }

  private modifyDropdownCollections(): void {
    if (this.hiddenDropdownData?.generationFacility) {
      this.specialAppliances = this.specialAppliances.filter(sa => sa.value !== 'generator');
    }
  }

  private resetData(): void {
    this.specialApplianceType = '';
    this.currentIndex = 0;
  }

  private updateAdditionalDataControls() {
    if (!this.specialApplianceFG) {
      return;
    }

    if (this.showAdditionalData) {
      const validatorsList = this.isSpecialApplianceSectionRequired ? [Validators.required] : [];

      this.specialApplianceFG.addControl('amount', new FormControl(this.specialApplianceData?.amount || '', validatorsList));
      if (!this.environmentService.copyExistingTechnicalDataConfiguration) {
        this.specialApplianceFG.addControl('typeName', new FormControl(this.specialApplianceData?.typeName || '', validatorsList));
        this.specialApplianceFG.addControl('manufacturer', new FormControl(this.specialApplianceData?.manufacturer || '', validatorsList));
      }
    } else {
      this.specialApplianceFG.removeControl('typeName');
      this.specialApplianceFG.removeControl('manufacturer');
      this.specialApplianceFG.removeControl('amount');
    }

    this.specialApplianceFG.updateValueAndValidity();
  }

  private validateZerezResponse(response: any) {
    // failed
    if (response instanceof HttpErrorResponse) {
      this.specialApplianceFG.get('zerez_id_validated').setValue(null); //'false' is considered as a valid value for a FormControl
      this.errorService.throwErrorIfFailure(response, true);
    }
    // successfull
    else {
      this.specialApplianceFG.get('zerez_id_validated').setValue(true);
      this.toaster.success('Zerez unit validated', 4000);
    }
    this.isLoading = false;
  }

  private showOrHideAdditionalSkiKeyAndDateOfCommissioningFields(show: boolean = false) {
    if (!this.showAdditionalSkiKeyAndDateOfCommissioning) return;

    if (show) {
      if (this.showConsumptionDeviceControl) {
        this.showConsumptionDeviceControlAndSkiKey();
      } else {
        this.addSkiKeyControl();
      }

      this.specialApplianceFG.addControl(
        'dateOfCommissioning',
        new FormControl(
          this.specialApplianceData?.date_of_commissioning ? new Date(this.specialApplianceData.date_of_commissioning) : null,
          [Validators.required]
        )
      );
    } else {
      if (this.specialApplianceFG.controls.skiKey) this.specialApplianceFG.removeControl('skiKey');
      if (this.specialApplianceFG.controls.dateOfCommissioning) this.specialApplianceFG.removeControl('dateOfCommissioning');
      if (this.specialApplianceFG.controls.consumptionDeviceControl) {
        this.specialApplianceFG.controls.consumptionDeviceControl.setValue(null);
        this.specialApplianceFG.controls.consumptionDeviceControl.clearValidators();
        this.specialApplianceFG.controls.consumptionDeviceControl.updateValueAndValidity();
      }
    }
  }

  private showConsumptionDeviceControlAndSkiKey(): void {
    if (!this.showConsumptionDeviceControl) return;

    if (this.specialApplianceFG.controls.consumptionDeviceControl) {
      this.specialApplianceFG.controls.consumptionDeviceControl.setValidators([Validators.required]);
      this.specialApplianceFG.controls.consumptionDeviceControl.updateValueAndValidity();
    } else {
      this.specialApplianceFG.addControl(
        'consumptionDeviceControl',
        new FormControl(this.specialApplianceData?.consumption_device_control || ConsumptionDeviceControl.potentialFreeRelayContact, [
          Validators.required,
        ])
      );
      if (this.specialApplianceData?.consumption_device_control === ConsumptionDeviceControl.digitalInterface) {
        this.addSkiKeyControl();
      }
      this.specialApplianceFG.controls.consumptionDeviceControl.valueChanges.subscribe(value => {
        if (value === ConsumptionDeviceControl.digitalInterface) {
          this.addSkiKeyControl();
        } else {
          if (this.specialApplianceFG.controls.skiKey) this.specialApplianceFG.removeControl('skiKey');
        }
      });
    }
  }

  private addSkiKeyControl(): void {
    this.specialApplianceFG.addControl('skiKey', new FormControl(this.specialApplianceData?.ski_key || '', [Validators.required]));
  }
}
